import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["gallery", "photosInput", "uploadButton"]

  connect() {
    this.cloudName = this.data.get("cloudNameValue") || ""
    this.uploadPreset = this.data.get("uploadPresetValue") || ""
    this.fieldName = this.data.get("fieldNameValue") || ""
    this.objectType = this.data.get("objectTypeValue") || ""
    this.objectId = this.data.get("objectIdValue") || ""
    this.fieldSlug = this.data.get("fieldSlugValue") || ""

    try {
      this.photos = JSON.parse(this.data.get("photosValue") || "[]")
    } catch (e) {
      this.photos = []
    }

    if (this.uploadPreset && this.cloudName) {
      if (this.hasUploadButtonTarget) this.uploadButtonTarget.disabled = true
      this.loadCloudinaryScript()
    }
    this.renderGallery()
    this.syncHiddenInputs()
    this._handleKeydown = this._handleKeydown.bind(this)
  }

  disconnect() {
    this.closeModal()
  }

  loadCloudinaryScript() {
    const scriptUrl = "https://upload-widget.cloudinary.com/global/all.js"
    const existingScript = document.querySelector(`script[src='${scriptUrl}']`)

    if (!existingScript) {
      const script = document.createElement("script")
      script.src = scriptUrl
      script.type = "text/javascript"
      script.async = true
      script.onload = () => this.createWidget()
      script.onerror = () => {
        console.error("Failed to load Cloudinary upload widget script")
        if (this.hasUploadButtonTarget) this.uploadButtonTarget.disabled = false
      }
      document.head.appendChild(script)
    } else if (typeof cloudinary !== "undefined") {
      this.createWidget()
    } else {
      existingScript.addEventListener("load", () => this.createWidget())
    }
  }

  createWidget() {
    if (typeof cloudinary === "undefined") return

    try {
      this.widget = cloudinary.createUploadWidget({
        cloudName: this.cloudName,
        upload_preset: this.uploadPreset,
        sources: ["local", "camera"],
        resourceType: "image",
        maxFileSize: 10000000,
        cropping: false,
        multiple: true,
        styles: {
          palette: {
            window: "#FFFFFF",
            windowBorder: "#90A0B3",
            tabIcon: "#1891F7",
            menuIcons: "#5A616A",
            textDark: "#0F2E51",
            textLight: "#FFFFFF",
            link: "#1891F7",
            action: "#FF620C",
            inactiveTabIcon: "#3F5A81",
            error: "#F55A68",
            inProgress: "#0078FF",
            complete: "#34D09B",
            sourceBg: "#e0e4f5"
          },
          fonts: {
            default: null,
            "'Poppins', sans-serif": {
              url: "https://fonts.googleapis.com/css?family=Poppins",
              active: true
            }
          }
        }
      }, (error, result) => {
        if (error) {
          console.error("Cloudinary upload error:", error)
          return
        }
        if (result && result.event === "success") {
          this.addPhoto({
            public_id: result.info.public_id,
            original_filename: result.info.original_filename
          })
        }
      })
    } catch (e) {
      console.error("Failed to create Cloudinary upload widget:", e)
      return
    }

    if (this.hasUploadButtonTarget) this.uploadButtonTarget.disabled = false
    if (this._pendingOpen) {
      this._pendingOpen = false
      this.widget.open()
    }
  }

  upload() {
    if (this.widget) {
      this.widget.open()
      return
    }
    // Script may still be loading; queue the open for when it finishes
    if (typeof cloudinary === "undefined") {
      this._pendingOpen = true
      this.loadCloudinaryScript()
      return
    }
    this.createWidget()
    if (this.widget) {
      this.widget.open()
    }
  }

  addPhoto(photo) {
    this.photos = [...this.photos, photo]
    this.renderGallery()
    this.syncHiddenInputs()
  }

  async removePhoto(event) {
    event.preventDefault()
    const publicId = event.currentTarget.dataset.publicId
    if (!confirm("Are you sure you want to remove this photo?")) return

    try {
      // Only call server deletion for saved records with full context.
      // Unsaved-record photos are removed from the UI only; Cloudinary
      // lifecycle rules handle orphaned uploads.
      if (this.objectId && this.objectType && this.fieldSlug) {
        await this.destroyFromCloudinary(publicId)
      }
      this.photos = this.photos.filter(p => p.public_id !== publicId)
      this.renderGallery()
      this.syncHiddenInputs()
    } catch (error) {
      console.error("Failed to delete photo from Cloudinary:", error)
    }
  }

  async destroyFromCloudinary(publicId) {
    const csrfToken = document.getElementsByName("csrf-token")[0]
    if (!csrfToken) throw new Error("CSRF token not found — cannot delete photo")

    const response = await fetch("/input_field_photos", {
      method: "DELETE",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken.content,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        public_id: publicId,
        object_type: this.objectType,
        object_id: this.objectId,
        field_slug: this.fieldSlug
      })
    })

    if (!response.ok) {
      throw new Error(`Delete failed with status ${response.status}`)
    }
  }

  renderGallery() {
    if (!this.hasGalleryTarget) return

    const photos = (this.photos || []).filter(p => p && typeof p === "object" && typeof p.public_id === "string" && p.public_id)
    if (photos.length === 0) {
      this.galleryTarget.innerHTML = '<span class="text-gray-500 text-sm">No photos uploaded.</span>'
      return
    }

    this.galleryTarget.innerHTML = photos.map(photo => {
      const encodedId = photo.public_id.split("/").map(encodeURIComponent).join("/")
      const imgUrl = `https://res.cloudinary.com/${encodeURIComponent(this.cloudName)}/image/upload/c_thumb,h_150,w_150/${encodedId}`
      return `
        <div class="inline-block relative group">
          <img src="${imgUrl}" alt="${this.escapeAttr(photo.original_filename || "Uploaded photo")}"
               class="rounded border border-gray-200 cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-shadow"
               width="150" height="150"
               role="button" tabindex="0"
               data-action="click->photo-field#openModal keydown->photo-field#openModal"
               data-public-id="${this.escapeAttr(photo.public_id)}"
               data-filename="${this.escapeAttr(photo.original_filename || "Photo")}">
          <p class="text-xs text-gray-500 mt-1 truncate max-w-[150px]">${this.escapeHtml(photo.original_filename)}</p>
          <button type="button"
                  aria-label="${photo.original_filename ? `Remove photo ${this.escapeAttr(photo.original_filename)}` : "Remove photo"}"
                  class="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  data-action="click->photo-field#removePhoto"
                  data-public-id="${this.escapeAttr(photo.public_id)}">
              &times;
          </button>
        </div>
      `
    }).join("")
  }

  openModal(event) {
    if (this._modalOverlay) return
    if (event.target.closest("button")) return
    if (event.type === "keydown") {
      if (event.key !== "Enter" && event.key !== " ") return
      event.preventDefault()
    }

    const publicId = event.currentTarget.dataset.publicId
    const filename = event.currentTarget.dataset.filename || "Photo"
    if (!publicId || typeof publicId !== "string") return

    this._previouslyFocused = document.activeElement

    const encodedId = publicId.split("/").map(encodeURIComponent).join("/")
    const encodedCloudName = encodeURIComponent(this.cloudName)
    const fullUrl = `https://res.cloudinary.com/${encodedCloudName}/image/upload/c_limit,w_1600,h_1200,q_auto/${encodedId}`

    const overlay = document.createElement("div")
    overlay.className = "fixed inset-0 z-50 flex items-center justify-center p-4"
    overlay.style.backgroundColor = "rgba(0, 0, 0, 0.75)"
    overlay.setAttribute("role", "dialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.setAttribute("aria-label", filename)

    overlay.innerHTML = `
      <div class="relative max-w-full max-h-full flex flex-col items-center">
        <button type="button"
                class="absolute -top-2 -right-2 bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center text-lg shadow-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white z-10"
                aria-label="Close">
          &times;
        </button>
        <img src="${fullUrl}"
             alt="${this.escapeAttr(filename)}"
             style="max-width: 100%; max-height: 85vh; object-fit: contain;"
             class="rounded shadow-2xl">
        <p class="text-white text-sm mt-2 text-center">${this.escapeHtml(filename)}</p>
      </div>`

    document.body.appendChild(overlay)
    document.body.style.overflow = "hidden"
    this._modalOverlay = overlay

    // Use direct event listeners — the overlay is outside this controller's
    // element so Stimulus data-action attributes won't resolve.
    overlay.addEventListener("click", () => this.closeModal())
    const contentDiv = overlay.querySelector("div")
    if (contentDiv) contentDiv.addEventListener("click", (e) => e.stopPropagation())
    const closeBtn = overlay.querySelector("button")
    if (closeBtn) closeBtn.addEventListener("click", () => this.closeModal())
    document.addEventListener("keydown", this._handleKeydown)

    if (closeBtn) closeBtn.focus()
  }

  closeModal() {
    if (!this._modalOverlay) return

    document.removeEventListener("keydown", this._handleKeydown)
    document.body.style.overflow = ""
    this._modalOverlay.remove()
    this._modalOverlay = null

    if (this._previouslyFocused) {
      this._previouslyFocused.focus()
      this._previouslyFocused = null
    }
  }

  _handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeModal()
    }
  }

  syncHiddenInputs() {
    if (!this.hasPhotosInputTarget) return

    const container = this.photosInputTarget.parentElement
    const fieldName = this.fieldName

    container.querySelectorAll("input[data-photo-hidden]").forEach(el => el.remove())

    const photos = (this.photos || []).filter(p => p && typeof p === "object" && typeof p.public_id === "string" && p.public_id)
    if (photos.length === 0) {
      this.photosInputTarget.name = `${fieldName}[]`
      this.photosInputTarget.value = ""
      return
    }

    this.photosInputTarget.name = ""
    this.photosInputTarget.value = ""

    photos.forEach(photo => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = `${fieldName}[]`
      input.value = JSON.stringify(photo)
      input.dataset.photoHidden = "true"
      container.appendChild(input)
    })
  }

  escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  escapeAttr(str) {
    if (!str) return ""
    return str.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/'/g, "&#39;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
